import { ACCESS_HIERARCHY, AccessLevel, PermissionRequirement, ROLE_HIERARCHY, UserClaims, UserRole } from "@/types/auth";

/**
 * Checks if user claims meet the minimum permission requirements
 */
export function hasPermission(userClaims: UserClaims, requirement: PermissionRequirement): boolean {
	// Check role hierarchy
	const userRoleLevel = ROLE_HIERARCHY[userClaims.role];
	const requiredRoleLevel = ROLE_HIERARCHY[requirement.role];

	if (userRoleLevel < requiredRoleLevel) {
		return false;
	}

	// Check access level hierarchy
	const userAccessLevel = ACCESS_HIERARCHY[userClaims.access];
	const requiredAccessLevel = ACCESS_HIERARCHY[requirement.access];

	if (userAccessLevel < requiredAccessLevel) {
		return false;
	}

	// Check specific permissions if required
	if (requirement.permissions && requirement.permissions.length > 0) {
		const userPermissions = userClaims.permissions || [];
		return requirement.permissions.every((permission) => userPermissions.includes(permission));
	}

	return true;
}

/**
 * Checks if user has admin access (any level)
 */
export function isAdmin(userClaims: UserClaims): boolean {
	return userClaims.role === UserRole.ADMIN || userClaims.role === UserRole.SUPERADMIN;
}

/**
 * Checks if user has super admin access
 */
export function isSuperAdmin(userClaims: UserClaims): boolean {
	return userClaims.role === UserRole.SUPERADMIN;
}

/**
 * Checks if user has at least the specified access level
 */
export function hasAccessLevel(userClaims: UserClaims, level: AccessLevel): boolean {
	const userLevel = ACCESS_HIERARCHY[userClaims.access];
	const requiredLevel = ACCESS_HIERARCHY[level];
	return userLevel >= requiredLevel;
}

/**
 * Checks if user has at least the specified role
 */
export function hasRole(userClaims: UserClaims, role: UserRole): boolean {
	const userRoleLevel = ROLE_HIERARCHY[userClaims.role];
	const requiredRoleLevel = ROLE_HIERARCHY[role];
	return userRoleLevel >= requiredRoleLevel;
}

/**
 * Gets the default claims for a new user
 */
export function getDefaultClaims(): UserClaims {
	return {
		role: UserRole.USER,
		access: AccessLevel.BASIC,
		permissions: [],
	};
}

/**
 * Gets the default admin claims
 */
export function getDefaultAdminClaims(): UserClaims {
	return {
		role: UserRole.ADMIN,
		access: AccessLevel.BASIC,
		permissions: [],
	};
}

/**
 * Gets the default super admin claims
 */
export function getDefaultSuperAdminClaims(): UserClaims {
	return {
		role: UserRole.SUPERADMIN,
		access: AccessLevel.SUPER,
		permissions: [],
	};
}
